import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { LanguageService } from '@core/language/language.service';
import { ContactUsCMSDataContract } from '@models/contact-us.models';
import { MessageTitleAndContent } from '@shared/types/cms-frequently-used-shapes';
import { showValidation } from '@shared/ui/utils/form-control-utils/show-validation';
import { emailValidator } from '@shared/validators/directives/email-validator.directive';

import { unwrapBodyTextHierarchy } from '../shared/types/cms-payload-with-bodytext.transform';
import { CONTACT_US_FACADE, ContactUsFacade } from './store/contact-us.facade';
import { ContactUsPayload } from './store/types/contact-us.state.type';
import { unwrapComponentMainContent } from './types/contact-us.transform';
import { ContactUsDataGroupMainContent } from './types/contact-us.type';

@Component({
  selector: 'jb-contact-us',
  templateUrl: './contact-us.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactUsComponent {
  mainContent: ContactUsDataGroupMainContent;
  success: MessageTitleAndContent;
  failure: MessageTitleAndContent;
  mainForm: UntypedFormGroup;

  isSubmitted$ = this.contactUsFacade.isSubmitted;
  submissionSucceeded$ = this.contactUsFacade.submissionSucceeded;
  submissionFailed$ = this.contactUsFacade.submissionFailed;
  submissionInProgress$ = this.contactUsFacade.submissionInProgress;

  get fullName() {
    return this.mainForm.get('fullName');
  }

  get email() {
    return this.mainForm.get('email');
  }

  get topic() {
    return this.mainForm.get('topic');
  }

  get comments() {
    return this.mainForm.get('comments');
  }

  get confirmationCode() {
    return this.mainForm.get('confirmationCode');
  }

  get urgency() {
    return this.mainForm.get('urgency');
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private languageService: LanguageService,

    @Inject(CONTACT_US_FACADE) private contactUsFacade: ContactUsFacade,
  ) {
    this.createMainForm();
  }

  convertFormValuesToAPIValues(values) {
    const currentLanguage = this.languageService.getLanguage();

    const payload: ContactUsPayload = {
      formId_: 'Contact Us',
      fullName: values.fullName,
      emailAddress: values.email,
      topic_: values.topic,
      comments: values.comments,
      confirmationCode: values.confirmationCode,
      travelingWithin48Hours: values.urgency ? 'Yes' : 'No',
      language: currentLanguage,
    };

    return payload;
  }

  submit() {
    const formValues = this.mainForm.value;
    showValidation(this.mainForm);

    if (this.mainForm.valid) {
      const contactUsPayload = this.convertFormValuesToAPIValues(formValues);
      this.contactUsFacade.submit(contactUsPayload);
    }
  }

  private createMainForm(): void {
    this.mainForm = this.formBuilder.group({
      fullName: [undefined, Validators.required],
      email: [undefined, [Validators.required, emailValidator]],
      topic: [undefined, Validators.required],
      comments: [
        undefined,
        [
          Validators.required,
          // Currently we don't have error messages for a comment
          // with less than 150 characters, or more than
          // 1500 characters. Should update markup when the data
          // is available.
          Validators.minLength(150),
          Validators.maxLength(1500),
        ],
      ],
      confirmationCode: [undefined, [Validators.minLength(6)]],
      urgency: [false],
    });
  }
}

export const contactUsContainerResolver = (
  registry,
  data: ContactUsCMSDataContract,
) => {
  const mainContent = unwrapComponentMainContent(data);
  const success = unwrapBodyTextHierarchy(data.successMessage);
  const failure = data.failureMessage
    ? unwrapBodyTextHierarchy(data.failureMessage)
    : {
        content: '',
        title: '',
      };

  return {
    mainContent,
    success,
    failure,
  };
};
